import React, { useState, useEffect, useRef } from 'react';
import { getSyncTaskStatus, type SyncTaskStatus } from '../../services/data-integration-api';

interface SyncProgressBarProps {
  syncTaskId: string;
  onComplete?: (status: SyncTaskStatus) => void;
}

export default function SyncProgressBar({ syncTaskId, onComplete }: SyncProgressBarProps) {
  const [status, setStatus] = useState<SyncTaskStatus | null>(null);
  const [error, setError] = useState('');
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    const poll = async () => {
      try {
        const result = await getSyncTaskStatus(syncTaskId);
        setStatus(result);
        if (['completed', 'failed', 'partial'].includes(result.status)) {
          clearInterval(timerRef.current);
          onComplete?.(result);
        }
      } catch (e: any) {
        setError(e.message);
        clearInterval(timerRef.current);
      }
    };

    poll();
    timerRef.current = setInterval(poll, 2000);

    return () => clearInterval(timerRef.current);
  }, [syncTaskId, onComplete]);

  if (error) return <div className="text-destructive text-sm">查询状态失败: {error}</div>;
  if (!status) return <div className="text-muted-foreground text-sm">加载中...</div>;

  const isRunning = status.status === 'running';
  const bgClass = status.status === 'completed' ? 'bg-[var(--color-success-light)]'
    : status.status === 'failed' ? 'bg-destructive/10'
    : status.status === 'partial' ? 'bg-[var(--color-warning-light)]'
    : 'bg-muted';

  const barColorClass = isRunning ? 'bg-primary'
    : status.status === 'completed' ? 'bg-[var(--color-success)]'
    : 'bg-destructive';

  const statusLabel: Record<string, string> = {
    pending: '等待中', running: '同步中', completed: '完成',
    failed: '失败', partial: '部分成功',
  };

  return (
    <div className={`${bgClass} rounded-md p-3 mb-3`}>
      <div className="flex justify-between mb-2">
        <span className="font-semibold text-sm">
          {statusLabel[status.status] || status.status} ({status.sync_type === 'full' ? '全量' : '增量'})
        </span>
        <span className="text-sm">{status.progress_percent}%</span>
      </div>

      {/* 进度条 */}
      <div className="bg-muted rounded-md h-5 overflow-hidden mb-2">
        <div
          className={`${barColorClass} h-full rounded-md transition-[width] duration-300`}
          style={{ width: `${Math.min(100, status.progress_percent)}%` }}
        />
      </div>

      <div className="flex gap-4 text-[13px]">
        <span>总计: {status.total_count.toLocaleString()}</span>
        <span className="text-[var(--color-success)]">成功: {status.success_count.toLocaleString()}</span>
        <span className="text-destructive">失败: {status.failure_count.toLocaleString()}</span>
        {status.skip_count != null && status.skip_count > 0 && (
          <span className="text-muted-foreground">跳过: {status.skip_count.toLocaleString()}</span>
        )}
        {status.duration_ms != null && (
          <span>耗时: {(status.duration_ms / 1000).toFixed(1)}s</span>
        )}
      </div>

      {status.failures && status.failures.length > 0 && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[13px]">
            失败记录 ({status.failures.length})
          </summary>
          <ul className="text-xs max-h-[200px] overflow-auto mt-1">
            {status.failures.map((f, i) => (
              <li key={i}>
                <code className="font-mono">{f.source_record_id}</code>: {f.failure_reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
